import { friendlyObjectiveTitle } from '@upright/common/model/input/eu-taxonomy/friendly';
import { EuActivityTypes, EuObjectives } from '@upright/common/model/input/eu-taxonomy/types';
import { MetricNature } from '@upright/common/model/input/types';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { formatRatioAsPercentage } from '@upright/ui/common/generic/utilities/format';
import { InfoBoxTitleRow } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/info-boxes/info-box-title-row';
import { commonInfoBoxPadding } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/info-boxes/styles';
import { ThreePartInfoBox } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/info-boxes/three-part-info-box';
import { LensBoxMode } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/types';
import { getEuObjectiveIcon } from '@upright/ui/common/upright/core/impact-lenses/taxonomy/common/eu-objective-icon';
import { taxonomyLensLayout } from '@upright/ui/common/upright/core/impact-lenses/taxonomy/common/layout';
import { useTaxonomyLensContext } from '@upright/ui/common/upright/core/impact-lenses/taxonomy/common/use-taxonomy-lens-context';
import { MetricNatureIcon } from '@upright/ui/common/upright/model/output/scores/eu-taxonomy/metric-nature-icon';
import styled from 'styled-components';
import { useCarouselLayout } from '../../../impact-lens-profile/context';

export function TaxonomyIndicatorsInfoBox() {
  const ctx = useTaxonomyLensContext();
  const baseLayout = useCarouselLayout();
  const data = ctx?.data.taxonomyKpis;
  if (!data) {
    return <></>;
  }
  const layout = taxonomyLensLayout(baseLayout, data);
  if (baseLayout.boxMode !== LensBoxMode.SIDE) {
    return <></>;
  }
  const area = layout.infoBoxArea();
  return (
    <ThreePartInfoBox
      id="foo"
      x={area.x1}
      y={area.y1}
      width={area.width}
      height={area.height}
      contentTop={<InfoBoxTitleRow>Taxonomy indicators</InfoBoxTitleRow>}
      contentMiddle={<MainContent />}
      contentBottom={<></>}
    />
  );
}

function MainContent() {
  const objectives = [
    EuObjectives.mitigation,
    EuObjectives.adaptation,
    EuObjectives.pollution,
    EuObjectives.water,
    EuObjectives.biodiversity,
    EuObjectives.circular,
  ];
  return (
    <Main>
      {objectives.map(o => {
        return <IndicatorRow key={o} objective={o} />;
      })}
    </Main>
  );
}

function IndicatorRow(props: { objective: EuObjectives }) {
  const Icon = getEuObjectiveIcon(props.objective);
  const iconSize = 15;
  const iconStyle = { height: iconSize, width: iconSize, marginRight: 10, flexShrink: 0, flexGrow: 0 };

  const ctx = useTaxonomyLensContext();
  const data = ctx?.data.taxonomyKpis;
  if (!data) {
    return <></>;
  }

  function pickDatum() {
    return data?.[props.objective]?.[EuActivityTypes.any];
  }

  const datum = pickDatum();
  const alignment = datum?.alignment;
  const eligibilityNature = datum?.eligibility?.nature || MetricNature.UPRIGHT_ESTIMATE;

  return (
    <IndicatorRowRoot>
      <TopPart>
        <Icon stroke={BouColor.BLACK} style={iconStyle} />
        <ObjectiveTitle>{friendlyObjectiveTitle(props.objective)}</ObjectiveTitle>
      </TopPart>
      <BottomPart>
        <Placeholder style={iconStyle} />
        <Table>
          <tbody>
            <tr>
              <TitleCell>Eligibility</TitleCell>
              <FigureCell>{formatRatioAsPercentage(datum?.eligibility?.value, 1)}</FigureCell>
              <SourceCell>
                <MetricNatureIcon nature={eligibilityNature} year={datum?.eligibility?.year} />
              </SourceCell>
            </tr>
            <tr>
              <TitleCell>Alignment</TitleCell>
              <FigureCell>{formatRatioAsPercentage(alignment?.value, 1)}</FigureCell>
              {alignment ? (
                <SourceCell>
                  <MetricNatureIcon nature={alignment.nature} year={alignment.year} />
                </SourceCell>
              ) : undefined}
            </tr>
          </tbody>
        </Table>
      </BottomPart>
    </IndicatorRowRoot>
  );
}

const ObjectiveTitle = styled.div`
  font-weight: 500;
`;

const Main = styled.div`
  ${commonInfoBoxPadding};
  padding-top: 0;
  font-size: 11px;
`;

const Table = styled.table`
  border-collapse: collapse;
  td {
    padding-top: 2px;
    padding-bottom: 2px;
  }
`;

const FigureCell = styled.td`
  width: 5em;
`;

const TitleCell = styled.td`
  padding-right: 1em;
`;

const SourceCell = styled.td`
  padding-left: 1em;
`;

const IndicatorRowRoot = styled.div`
  padding-top: 10px;
  padding-bottom: 7px;
  & + & {
    border-top: 1px solid ${BouColor.TOOL_TABLE_BACKGROUND_LIGHT_GRAY};
  }
`;

const TopPart = styled.div`
  display: flex;
  align-items: center;
`;
const BottomPart = styled.div`
  display: flex;
  padding-top: 4px;
`;
const Placeholder = styled.div``;
